import { useVariableFiche } from '@/components/admin/variables/use-variable-fiche';
import { VariableFiche } from '@/components/admin/variables/variable-fiche';
import { buildPillElement } from '@/components/admin/variables/variable-pill';
import { Tooltip } from '@/components/ui/tooltip';
import { atQueryBefore } from '@/lib/rich-text-editor';
import { cn } from '@/lib/utils';
import type {
  BoundRefusal,
  PaintedPiece,
  StoredSelection,
  TokenLanding,
  VariableZone,
} from '@/lib/variable-pill';
import {
  boundRefusalSentence,
  insertAtCaret,
  paintSignature,
  paintedPieces,
  replaceAtQuery,
  selectStored,
  serializePainted,
  storedSelectionOf,
  variableOfPillToken,
} from '@/lib/variable-pill';
import { Braces } from 'lucide-react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Le champ PEINT : une zone de texte simple dont les jetons sont dessinés en
 * pastilles (refonte modulaire §8.6 ; grill Variables du 2026-09-16, D5 ;
 * maquette `variables.html`, planches 1 et 2 ; issue #88).
 *
 * The direction field of the protocol editor was the one zone that drew a pill
 * (§7.2), and this file is that field GENERALISED. It is the field of seven
 * zones: the directions of a protocol (`direction-field.tsx`, a thin dressing
 * of this one), the reading for the analyst (#158), the Contexte of the
 * protocol page (`protocol-page-panel.tsx`), the persona overlay
 * (`experience-persona-overlay.tsx`), and in `content-panel.tsx` a Link's
 * label, a Copyable text and a Checkbox label. The Texte block is NOT one of
 * them: it is the WYSIWYG, `rich-text-field.tsx`. What differs between the
 * zones - the palette they open, the names they resolve, where « Ouvrir »
 * leads - comes in as props; what they share - the paint, the read back, the
 * `@`, the fiche - lives here once.
 *
 * ## React does not own the children, and that is deliberate
 *
 * The div is rendered EMPTY and painted imperatively. A contentEditable whose
 * children React reconciles is rebuilt on every keystroke - the parent gets
 * `onChange`, hands the value back, React rebuilds the text node, and the caret
 * jumps to the start of it. So the DOM is the live surface, `painted` remembers
 * the SIGNATURE of what it shows (`paintSignature`: labels and inks included),
 * and the effect repaints only when the incoming value - or the name one of its
 * pills carries - is not already there. It is the régime `react-contenteditable`
 * exists for, written in twenty lines rather than pulled in as a dependency.
 *
 * `serializePainted` is the read back, and it lives in `lib/` because it is
 * the one rule of this field a unit test can hold: `innerText`, which the
 * mockup reads, is not implemented in jsdom at all.
 *
 * ## The palette opens on the TEXT BEFORE THE CARET
 *
 * `@` is not a key handler: a paste, an arrow key and a deletion all change
 * what sits before the caret, so the query is recomputed from the selection
 * after every input rather than from what was typed. Échap closes it, Entrée
 * inserts the first line - the two the query line promises. The menu itself is
 * the caller's (`renderMenu`): the protocol draws three groups of its own, an
 * experience four groups that follow the position (D3).
 *
 * Échap DISMISSES the `@` it closed on (#160). The keyup of that very key
 * recomputes the query, finds the same `@query` before the caret and would
 * open again what the keydown closed - which it did, in a real browser, until
 * #160. So the `@` stays dismissed while its word goes on, and a new `@` - or
 * a caret that leaves the word - opens the palette again.
 *
 * ## The `{ }` lands at the caret (#160)
 *
 * The button used to write at the end of the text, whatever the caret said.
 * It now lands where the field's caret is - the live one while the field has
 * the focus, since the button's `mousedown` keeps it there, or the LAST one the
 * field had, followed on `selectionchange` while it was focused, collapsed to
 * its end: a selection nobody sees any more is not one to replace - and at the
 * end only when the field never had one. A selection the field holds without
 * the focus is not a caret: a repaint leaves one at its start, and nobody put
 * it there. A text that comes from outside - an undo, a reload - forgets the
 * caret, which pointed into another text.
 *
 * Both gestures insert the same way: the token lands in the STORED text
 * (`replaceAtQuery`, `insertAtCaret` - where the spaces go is theirs), the
 * field repaints, and the caret comes back right after what was written, the
 * field focused first, since focusing a `contentEditable` moves its selection
 * to the start (`caretAfterPill` says the same). Surgery on the DOM left the
 * caret at the start of the text node it had just cut.
 *
 * ## The fiche is the zone's, delegated (D6)
 *
 * The pills are rebuilt on every repaint, so nothing is attached to them: the
 * handlers of `useVariableFiche` sit on the container and find the pill under
 * the pointer. The fiche asks `describe` for the standing of the token the
 * pill carries, so a pill and its fiche can never disagree on a name.
 *
 * ## The bound refuses, it never cuts (#158, #160)
 *
 * The bound is `maxLength`, or the `max` of the counter: a field that counts
 * its text is bounded by what it counts (#160). Each of the five counted zones
 * used to cut the text it was handed - `slice(0, MAX)` in its panel -, so a
 * keystroke mid-text ate the last character and the token of a final pill went
 * out broken; the reading, which draws its own line, passes `maxLength`.
 *
 * The bound weighs the STORED text - a `{{objectif:id}}` weighs its token,
 * not its label -, and a field at its bound loses nothing it already had. The
 * guard is `beforeinput`, where an input can still be refused whole: when the
 * projected length (the text, less the selection it replaces, plus what it
 * adds) passes the bound, the keystroke, the paste or the drop does not happen
 * and the caret does not move. It is a native listener, because React's
 * `onBeforeInput` is a polyfill that knows neither `inputType` nor a paste.
 *
 * The projection knows the MARKER a browser leaves on a new last line: Entrée
 * at the end of the text makes `<div><br></div>`, Maj+Entrée one `\n` more,
 * and the read back counts it - so a line break there adds two characters, and
 * the keystroke that then replaces the marker adds none (measured in Chrome,
 * second review of !478). What the projection cannot know - an IME
 * composition, a history step, a script - is judged at `input`: the last
 * accepted text comes back whole, the selection where `beforeinput` found it,
 * and the palette closes, its `@query` anchor gone with the repaint.
 *
 * A refusal is SAID: on the counter's line, in an `<output>` that stays
 * mounted, when the field draws that line; to `onRefused` in any case, for a
 * caller that draws its own. A count is text nobody hears change, and a paste
 * of 3500 characters that does nothing, without a word, reads as a broken
 * field. A pill is refused before it is built: `fits` tells the menu which
 * tokens still hold where they would land, so it greys the others. Only a
 * GROWTH past the bound is refused, so a text already over it can still shrink.
 *
 * ## The box
 *
 * The `{ }` sits in the bottom right corner of the BOX, the maquette's
 * `.field .tools` (#160): it is positioned against the box that holds the
 * field and its placeholder, never against the whole zone. Against the zone,
 * it fell on the counter's line, and under an experience's palette, which
 * opens in the flow, once that palette opened.
 */

export interface PaintedTextFieldProps {
  /** The text as it is STORED: literal runs plus `{{kind:id}}` tokens. */
  readonly value: string;
  readonly onChange: (next: string) => void;
  /** « Voir », a retired object, an overlay without persona: readable, selectable, not typable. */
  readonly readOnly: boolean;
  readonly placeholder: string;
  /** The field's accessible name, or the id of the label that gives it. */
  readonly ariaLabel?: string;
  readonly ariaLabelledBy?: string;
  /**
   * The ids of what describes the field, when its caller draws that itself -
   * the reading for the analyst names its encart and its two lines (#158).
   */
  readonly ariaDescribedBy?: string;
  readonly testId: string;
  /**
   * The mark of the `{ }` button, or null.
   *
   * A mark is read across seams (an e2e spec, a capture script), and several
   * fields carrying it on one screen would be a selector that resolves to
   * several elements. The button itself is on every field, because §8.6 puts
   * it on every text zone.
   */
  readonly insertTestId: string | null;
  /** The height of the box: a direction is taller than a label. */
  readonly minHeightClass: string;
  /** What each token IS here, for the pill and for its fiche (D5, D6), and where « Ouvrir » lands. */
  readonly zone: VariableZone;
  /**
   * The menu under the field while it is open: given the letters typed after
   * the `@` - null when the `{ }` button opened it -, how to insert a token,
   * and whether a token still FITS under the bound where it would land
   * (always true without a bound), for a menu that greys what would be refused.
   */
  readonly renderMenu: (
    query: string | null,
    insert: (token: string) => void,
    fits: (token: string) => boolean,
  ) => ReactNode;
  /** What Entrée inserts while the menu is open, or null when there is nothing. */
  readonly firstToken: (query: string) => string | null;
  /**
   * The line under the box - the `@` hint on the left, the counter on the
   * right, a refusal under the hint - and the BOUND the counter counts
   * against: a field that counts its text is bounded by what it counts (#160).
   * Absent, the field draws none - a protocol's direction has no counter.
   */
  readonly counter?: { readonly max: number; readonly testId?: string } | undefined;
  /** What the left of that line says; the `@` hint by default. */
  readonly hint?: string | undefined;
  /**
   * A bound of the STORED text without the counter's line, tokens counted at
   * their stored length - the reading for the analyst, which draws its own
   * line (#158). A keystroke, a paste or a pill that would pass it is REFUSED,
   * never cut (see « The bound » in the head).
   */
  readonly maxLength?: number | undefined;
  /**
   * Told when the bound refuses an input: where it came from, what it would
   * have added, the room left. The counter's line says it already; a caller
   * that draws its own line says it there - the reading, in an `<output>` whose
   * role is `status` (#158).
   */
  readonly onRefused?: ((refusal: BoundRefusal) => void) | undefined;
  readonly className?: string | undefined;
}

/** The `@query` the caret sits at the end of: what was typed after the `@`, and where it stands. */
type AtQuery = { readonly typed: string; readonly span: StoredSelection };

/** Where a token will land: the `@query` it replaces, or the caret it goes at. */
type Target = { readonly query: boolean; readonly span: StoredSelection };

/** The stored text once `token` has landed on `at`, and where the caret goes. */
function land(text: string, at: Target, token: string): TokenLanding {
  return at.query ? replaceAtQuery(text, at.span, token) : insertAtCaret(text, at.span, token);
}

/**
 * The `@query` right before a collapsed caret in `host`, read off the TEXT
 * NODE the caret is in - a pill before the `@` can never become part of the
 * query - and located in stored characters, where the token will replace it.
 */
function atQueryAtCaret(host: HTMLElement): AtQuery | null {
  const selection = window.getSelection();
  const at = selection?.anchorNode ?? null;
  if (selection === null || !selection.isCollapsed) return null;
  if (at === null || at.nodeType !== Node.TEXT_NODE || !host.contains(at)) return null;
  const typed = atQueryBefore((at.textContent ?? '').slice(0, selection.anchorOffset));
  const caret = typed === null ? null : storedSelectionOf(host);
  if (typed === null || caret === null) return null;
  // The `@` goes with the query it opened: `end - length - 1`.
  return { typed, span: { start: caret.end - typed.length - 1, end: caret.end } };
}

/** Entrée or Maj+Entrée: the two inputs that leave a marker at the end of the text. */
function breaksLine(event: InputEvent): boolean {
  return event.inputType === 'insertParagraph' || event.inputType === 'insertLineBreak';
}

/**
 * The stored text an input event would ADD, or null for one the guard lets
 * through: a deletion, a format, and a composition, which is judged at
 * `input`. A line break is one character, as `serializePainted` reads it back
 * - two at the very end of the text, where the browser leaves a marker on the
 * new line (see « The bound » in the head); a paste or a drop is read off its
 * plain text; what cannot be known before it happens (a history step) counts
 * as nothing, and the `input` that follows is where it is judged.
 */
function insertedText(
  event: InputEvent,
  text: string,
  spot: StoredSelection | null,
): string | null {
  const kind = event.inputType;
  if (kind.startsWith('delete') || kind.startsWith('format')) return null;
  if (kind === 'insertCompositionText') return null;
  if (breaksLine(event)) {
    const atEnd =
      spot !== null &&
      spot.start === spot.end &&
      spot.end === text.length &&
      text !== '' &&
      !text.endsWith('\n');
    return atEnd ? '\n\n' : '\n';
  }
  return event.data ?? event.dataTransfer?.getData('text/plain') ?? '';
}

/**
 * Whether the caret sits on the MARKER of an empty last line - right before
 * the final `\n` a line break at the end left there - where what is typed
 * replaces it: one stored character the input gives back. A real `\n` in the
 * same place is counted free too, and the `input` judges that rare case.
 */
function onMarker(event: InputEvent, text: string, spot: StoredSelection | null): boolean {
  return (
    !breaksLine(event) &&
    spot !== null &&
    spot.start === spot.end &&
    text.endsWith('\n') &&
    spot.start === text.length - 1
  );
}

/** Where a refused input came from, for the sentence that says what was not added. */
function refusalSource(event: InputEvent): BoundRefusal['by'] {
  if (event.inputType.startsWith('insertFromPaste')) return 'paste';
  return event.inputType === 'insertFromDrop' ? 'drop' : 'input';
}

/** Whether `projected` passes `maxLength` by GROWING past it - a shrink always holds. */
function refused(maxLength: number, current: number, projected: number): boolean {
  return projected > maxLength && projected > current;
}

function paint(host: HTMLElement, pieces: readonly PaintedPiece[]): void {
  host.replaceChildren();
  for (const piece of pieces) {
    host.append(
      piece.kind === 'text' ? document.createTextNode(piece.text) : buildPillElement(piece.paint),
    );
  }
}

export function PaintedTextField({
  value,
  onChange,
  readOnly,
  placeholder,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  testId,
  insertTestId,
  minHeightClass,
  zone,
  renderMenu,
  firstToken,
  counter,
  hint,
  maxLength,
  onRefused,
  className,
}: PaintedTextFieldProps) {
  const host = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);
  /** What the DOM currently shows, as the signature of its pieces. */
  const painted = useRef<string | null>(null);
  /** The last text the field held within its bound: what a refused `input` restores. */
  const accepted = useRef(value);
  /** The selection the last `beforeinput` found: where a refused `input` puts it back. */
  const before = useRef<StoredSelection | null>(null);
  /** The last caret the field had while it held the focus: where a `{ }` lands (#160). */
  const lastCaret = useRef<StoredSelection | null>(null);
  /** The `@query` the open palette replaces, in stored characters - null when `{ }` opened it. */
  const anchor = useRef<StoredSelection | null>(null);
  /** Where the `@` stands that Échap closed the palette on, so its keyup does not open it again. */
  const dismissed = useRef<number | null>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  /** The last refusal of the bound, with the text it was said on (#160). */
  const [said, setSaid] = useState<{ readonly at: string; readonly sentence: string } | null>(null);
  const fiche = useVariableFiche();
  const { describe } = zone;
  const bound = maxLength ?? counter?.max;
  const counted = counter !== undefined;

  /** A refusal of the bound: said on the counter's line when the field draws it, and told. */
  const refuse = (refusal: BoundRefusal): void => {
    if (counted && bound !== undefined) {
      setSaid({ at: accepted.current, sentence: boundRefusalSentence(refusal, bound, 'le champ') });
    }
    onRefused?.(refusal);
  };
  /** `refuse` of the last render, for the native listener the guard subscribes once. */
  const reportRefusal = useRef(refuse);

  const remember = useCallback(
    (text: string) => {
      painted.current = paintSignature(paintedPieces(text, describe));
    },
    [describe],
  );

  const close = useCallback(() => {
    setOpen(false);
    setQuery(null);
    anchor.current = null;
  }, []);

  useEffect(() => {
    // A text that did not come from this field - an undo, a reload - is not
    // the one its caret or its `@query` pointed into.
    const outside = value !== accepted.current;
    accepted.current = value;
    if (outside) {
      lastCaret.current = null;
      dismissed.current = null;
      close();
    }
    const node = host.current;
    if (node === null) return;
    const pieces = paintedPieces(value, describe);
    const signature = paintSignature(pieces);
    // Already on screen: repainting would move the caret to the start of the
    // text node it just rebuilt, which is the defect this whole file avoids.
    if (painted.current === signature) return;
    paint(node, pieces);
    painted.current = signature;
  }, [value, describe, close]);

  useEffect(() => {
    reportRefusal.current = refuse;
  });

  // The bound's guard: refused here, an input leaves the DOM and the caret as
  // they were (see « The bound » in the head).
  useEffect(() => {
    const node = host.current;
    if (node === null || readOnly || bound === undefined) return;
    const onBeforeInput = (event: InputEvent): void => {
      const text = serializePainted(node);
      const spot = storedSelectionOf(node);
      before.current = spot;
      const added = insertedText(event, text, spot);
      if (added === null) return;
      const kept = text.length - (spot === null ? 0 : spot.end - spot.start);
      const projected = kept - (onMarker(event, text, spot) ? 1 : 0) + added.length;
      if (!refused(bound, text.length, projected)) return;
      event.preventDefault();
      before.current = null;
      reportRefusal.current({
        by: refusalSource(event),
        added: added.length,
        room: Math.max(0, bound - kept),
      });
    };
    node.addEventListener('beforeinput', onBeforeInput);
    return () => node.removeEventListener('beforeinput', onBeforeInput);
  }, [bound, readOnly]);

  // The caret the `{ }` will land on, followed while the field has the focus
  // (see « The `{ }` lands at the caret » in the head).
  useEffect(() => {
    if (readOnly) return;
    const onSelection = (): void => {
      const node = host.current;
      if (node === null || !node.contains(document.activeElement)) return;
      const spot = storedSelectionOf(node);
      if (spot !== null) lastCaret.current = spot;
    };
    document.addEventListener('selectionchange', onSelection);
    return () => document.removeEventListener('selectionchange', onSelection);
  }, [readOnly]);

  /*
    A click anywhere else shuts the palette, on `mousedown` and not on `click`
    for the reason `action-menu.tsx` states: the very event that opens it would
    otherwise bubble to the document and close it again in the same tick.
  */
  useEffect(() => {
    if (!open) return;
    function onDown(event: MouseEvent) {
      const target = event.target;
      if (target instanceof Node && container.current?.contains(target) === true) return;
      close();
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  /** Opens the palette on the `@query` before the caret, or closes the one it had opened. */
  const watchAt = useCallback(
    (node: HTMLElement) => {
      const found = atQueryAtCaret(node);
      if (found === null) {
        dismissed.current = null;
        if (query !== null) close();
        return;
      }
      // Échap closed the palette on this very `@`: it stays shut while the word goes on.
      if (dismissed.current === found.span.start) return;
      dismissed.current = null;
      anchor.current = found.span;
      setQuery(found.typed);
      setOpen(true);
    },
    [close, query],
  );

  function handleInput(): void {
    const node = host.current;
    if (node === null) return;
    const text = serializePainted(node);
    const spot = before.current;
    before.current = null;
    const kept = accepted.current;
    if (bound !== undefined && refused(bound, kept.length, text.length)) {
      // What `beforeinput` could not stop: the last accepted text comes back
      // whole, pills rebuilt, the selection where `beforeinput` found it - and
      // the palette closes, its `@query` anchor gone with the repaint.
      paint(node, paintedPieces(kept, describe));
      remember(kept);
      close();
      selectStored(node, spot ?? { start: kept.length, end: kept.length });
      refuse({
        by: 'input',
        added: text.length - kept.length,
        room: Math.max(0, bound - kept.length),
      });
      return;
    }
    accepted.current = text;
    remember(text);
    onChange(text);
    watchAt(node);
  }

  /**
   * Where a token put NOW would land in `text` (see the head): in place of the
   * `@query` the palette was opened on; else at the caret - the live one while
   * the field has the focus, else the last it had, collapsed to its end, since
   * a selection nobody sees any more is not one to replace -; else at the end.
   */
  const target = useCallback((text: string): Target => {
    const query = anchor.current;
    if (query !== null) return { query: true, span: query };
    const node = host.current;
    const live = node?.contains(document.activeElement) === true ? storedSelectionOf(node) : null;
    if (live !== null) return { query: false, span: live };
    const at = lastCaret.current?.end ?? text.length;
    return { query: false, span: { start: at, end: at } };
  }, []);

  /*
    Whether `token` still holds under the bound where `insert` would put it.
    Every line of the menu asks, so the target is read ONCE a render, while a
    bounded palette is open - reading it per line cloned the field each time.
  */
  const shownTarget = open && bound !== undefined ? target(value) : null;
  const fits = (token: string): boolean =>
    bound === undefined ||
    shownTarget === null ||
    !refused(bound, value.length, land(value, shownTarget, token).text.length);

  const insert = useCallback(
    (token: string): void => {
      const node = host.current;
      if (node === null || variableOfPillToken(token.slice(2, -2)) === null) return;
      const text = serializePainted(node);
      const landed = land(text, target(text), token);
      if (bound !== undefined && refused(bound, text.length, landed.text.length)) return;
      paint(node, paintedPieces(landed.text, describe));
      remember(landed.text);
      accepted.current = landed.text;
      dismissed.current = null;
      close();
      onChange(landed.text);
      // The caret right after what was written, the field focused FIRST: a
      // focus moves the selection of a `contentEditable` to its start.
      const caret = { start: landed.caret, end: landed.caret };
      node.focus();
      selectStored(node, caret);
      lastCaret.current = caret;
    },
    [bound, describe, target, remember, onChange, close],
  );

  function handleKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    fiche.handlers.onKeyDown(event);
    if (!open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      // The keyup of this very key reads the text before the caret again and
      // would open, on the same `@query`, what Échap closes here - so its `@`
      // is remembered as dismissed, whichever gesture opened the palette.
      const node = host.current;
      const found = node === null ? null : atQueryAtCaret(node);
      dismissed.current = found === null ? null : found.span.start;
      close();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const first = firstToken(query ?? '');
      if (first !== null) insert(first);
    }
  }

  const standing = useMemo(() => {
    if (fiche.anchor === null) return null;
    const variable = variableOfPillToken(fiche.anchor.token);
    return variable === null ? null : describe(variable);
  }, [fiche.anchor, describe]);

  return (
    <div ref={container} className={cn('relative', className)}>
      {/* The BOX - the field, its placeholder and its `{ }` -, which the button
          is positioned against (see « The box » in the head). A column that
          grows, for a caller that stretches the field into a height. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={host}
          data-testid={testId}
          role="textbox"
          aria-multiline="true"
          {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
          {...(ariaLabelledBy === undefined ? {} : { 'aria-labelledby': ariaLabelledBy })}
          {...(ariaDescribedBy === undefined ? {} : { 'aria-describedby': ariaDescribedBy })}
          aria-readonly={readOnly}
          contentEditable={!readOnly}
          suppressContentEditableWarning
          tabIndex={0}
          // The fiche's delegated handlers first, so the field's own `onKeyDown`
          // - which calls the fiche's before its Échap and Entrée - wins the key.
          {...fiche.handlers}
          onInput={handleInput}
          onKeyUp={() => {
            const node = host.current;
            if (node !== null && !readOnly) watchAt(node);
          }}
          onKeyDown={handleKeyDown}
          className={cn(
            'w-full whitespace-pre-wrap break-words rounded-md border-2 border-gray-300 p-3 text-sm leading-relaxed focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            readOnly ? 'bg-gray-50 text-gray-700' : 'bg-white pb-11 text-gray-800',
            minHeightClass,
          )}
        />
        {/* The placeholder is a sibling and not a `::before`: `content: attr(...)`
            cannot be asserted by a test, and the field's own name is on
            `aria-label` anyway. */}
        {value === '' ? (
          <p
            aria-hidden="true"
            className="pointer-events-none absolute top-3 left-3 text-gray-400 text-sm leading-relaxed"
          >
            {placeholder}
          </p>
        ) : null}
        {readOnly ? null : (
          <div className="absolute right-2 bottom-2 flex gap-1">
            <Tooltip label="Insérer une variable">
              <button
                type="button"
                {...(insertTestId === null ? {} : { 'data-testid': insertTestId })}
                aria-label="Insérer une variable"
                // The field keeps its caret: a blur here would lose the spot the
                // pill is meant to land on.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  anchor.current = null;
                  setQuery(null);
                  setOpen((was) => !was);
                }}
                className="inline-flex size-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Braces aria-hidden="true" className="size-4" />
              </button>
            </Tooltip>
          </div>
        )}
      </div>
      {counter === undefined ? null : (
        <div className="mt-1.5 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-gray-600 text-xs leading-4">
              {hint ?? (readOnly ? '' : 'Tape @ pour insérer une variable')}
            </p>
            {/* An `<output>`, whose role is `status`, mounted while the field
                is typable and EMPTY until a refusal: a live region that
                appears with its text is one some screen readers never
                announce. The sentence holds for the text it was said on. */}
            {readOnly ? null : (
              <output className="mt-1 block font-semibold text-rouge-deep text-xs leading-4 empty:mt-0">
                {said !== null && said.at === value ? said.sentence : null}
              </output>
            )}
          </div>
          <span
            {...(counter.testId === undefined ? {} : { 'data-testid': counter.testId })}
            className={cn(
              'shrink-0 text-xs leading-4',
              value.length > counter.max ? 'font-semibold text-rouge-deep' : 'text-gray-500',
            )}
          >
            {value.length} / {counter.max} caractères
          </span>
        </div>
      )}
      {open && !readOnly ? renderMenu(query, insert, fits) : null}
      {fiche.anchor !== null && standing !== null ? (
        <VariableFiche
          standing={standing}
          place={zone.place}
          experienceId={zone.experienceId}
          anchor={fiche.anchor.element}
          pinned={fiche.pinned}
          ficheRef={fiche.ficheRef}
        />
      ) : null}
    </div>
  );
}
